/**
 * ─── THE LADDER: lifetime progression, one curve for every format ────────────
 *
 * `shared/rules.ts` scoring is the per-MATCH currency (team totals live on the
 * engine and die with the match). This module is the per-PLAYER one: points
 * accumulated forever, turned into a level. `progression.ts` converts a finished
 * seat into the XP that lands here; this file is only the curve and the grants.
 *
 * Ported verbatim (arithmetic-wise) from the ringmaster project's
 * `packages/shared/src/levels.ts` — every number, boundary rule and grant value
 * is identical, so the two systems can share a rebuild script's reasoning.
 *
 * THE ONE INVARIANT: level is a pure function of lifetime XP. It is stored on
 * `player_profiles.level` only as a read cache — recomputed on every write, never
 * authoritative. That is what lets this curve be retuned later and the whole
 * history replayed (see server/scripts/rebuild-profiles.ts).
 */
/**
 * Cost of the step OUT OF level L, i.e. `cost(1)` is what level 1 asks for.
 *
 *     cost(L) = 250 + 50·L        →  total(L) = 25·(L−1)·(L+10)
 *
 * The shape is arithmetic-increment (the classic RPG curve): early levels land
 * inside a single sitting, later ones ask for a few. `cost(1) = 300` is the
 * user-set anchor — see FIRST_GAME_BONUS for why a first game clears it.
 *
 * The table below is the TUNABLE ARTIFACT: edit a number here and the whole
 * system follows (the formula survives only as a test oracle). Levels past the
 * table extrapolate at a flat `TAIL_COST` rather than growing forever, so a
 * heavy player never hits a wall that stops moving.
 */
export const LEVEL_XP = [
    0, 300, 650, 1050, 1500, 2000, 2550, 3150, 3800, 4500,
    5250, 6050, 6900, 7800, 8750, 9750, 10800, 11900, 13050, 14250,
    15500, 16800, 18150, 19550, 21000, 22500, 24050, 25650, 27300, 29000,
    30750, 32550, 34400, 36300, 38250, 40250, 42300, 44400, 46550, 48750,
    51000, 53300, 55650, 58050, 60500, 63000, 65550, 68150, 70800, 73500,
];
/** Highest level the table spells out. Past this, `TAIL_COST` per level. */
export const MAX_TABLE_LEVEL = LEVEL_XP.length; // 50
/** Flat cost of every level past the table — `cost(50)`, held constant. */
export const TAIL_COST = 2750;
/**
 * One-time grant on a player's FIRST finished match ("first game").
 *
 * WHAT 100 BUYS, exactly — the arithmetic is worth knowing before changing it.
 * `progression.ts` bounds a single match's XP from both sides: a winner banks at
 * least `WIN_BASE_FLOOR + WIN_BONUS` (200) and at most `max(MATCH_XP_CAP) +
 * WIN_BONUS` (520); a loser banks at most `LOSS_CAP` (150). So with a 100 grant a
 * first match lands in two bands, against level 1's 300:
 *
 *     winner   200..520 match + 100  =  300..620   → LEVELS, always
 *     loser      0..150 match + 100  =  100..250   → stays level 1, always
 *
 * That split is the DESIGN, not a shortfall: **winning your first game levels
 * you; losing it does not.** The level-up is something you earn rather than
 * something you are handed for showing up, which is why this is 100 and not the
 * 300 it takes to guarantee a crossing for everybody.
 *
 * Two properties the value must keep, both pinned in levels.test.ts:
 *  - a first game can never DOUBLE-level: 520 + 100 = 620 < 650.
 *  - the WORST win still levels. A floor win with no nils plus the grant lands on
 *    exactly 300, and `levelProgress` treats reaching a threshold AS the
 *    level-up — so that seat gets level 2 with an empty bar. Under the opposite
 *    boundary rule (exact threshold = the level below, full bar) the same seat
 *    would have been handed a grant that visibly did nothing.
 *
 * Minted by the profile layer only: it is never a team total and never reaches
 * the engine's scoreboard.
 */
export const FIRST_GAME_BONUS = 100;
/**
 * The same grant, paid again on a player's first match of a NEW DAY.
 *
 * Mechanically identical to FIRST_GAME_BONUS — same value, same effect on the
 * bands above — and different only in what the end screen calls it. One is a
 * welcome, the other a welcome BACK.
 *
 * At 100 it is a nudge rather than a payday: less than a won match and roughly a
 * made nil, so coming back is rewarded without making levels a measure of
 * attendance alone. It is also paid at most once per day-gap, so a long session
 * cannot farm it.
 *
 * "A new day" is a GAP, not a calendar date — see DAILY_BONUS_GAP_HOURS.
 */
export const DAILY_RETURN_BONUS = 100;
/**
 * How long a player must be away before their next match counts as a new day.
 *
 * A GAP rather than a calendar date: calendar days lie about sessions. A UTC date
 * boundary falls at 5pm Pacific — squarely inside a game night — so one sitting
 * would straddle it and pay the "daily" bonus TWICE, while a group playing
 * 11pm→1am would be handed a second one an hour in. A gap cannot do either,
 * needs no timezone we do not have, and cannot be farmed by waiting for midnight.
 *
 * 12 hours: last night's session never blocks tonight's (evening→evening is
 * ~22h), but two sittings in one day are never both rewarded (a 10am match does
 * not unlock an 8pm one).
 */
export const DAILY_BONUS_GAP_HOURS = 12;
/** The same gap in milliseconds — the only unit the server ever compares in. */
export const DAILY_BONUS_GAP_MS = DAILY_BONUS_GAP_HOURS * 60 * 60 * 1000;
/** Points for a grant kind — the one place the two values are compared. */
export function bonusXp(kind) {
    return kind === 'first_game' ? FIRST_GAME_BONUS : DAILY_RETURN_BONUS;
}
/** Lifetime XP required to STAND IN `level` (level 1 = 0). */
export function xpForLevel(level) {
    const l = Math.max(1, Math.floor(level));
    if (l <= MAX_TABLE_LEVEL)
        return LEVEL_XP[l - 1];
    return LEVEL_XP[MAX_TABLE_LEVEL - 1] + (l - MAX_TABLE_LEVEL) * TAIL_COST;
}
/** XP the step out of `level` asks for — the progress bar's denominator. */
export function xpCostOfLevel(level) {
    return xpForLevel(level + 1) - xpForLevel(level);
}
/** Colour band. One per 5 levels: 1–4, 5–9, 10–14, 15–19, 20–24, 25+. */
export const MAX_TIER = 5;
export function tierForLevel(level) {
    return Math.min(Math.max(0, Math.floor(level / 5)), MAX_TIER);
}
/**
 * Where `totalXp` puts a player on the curve.
 *
 * Reaching a threshold IS the level-up: `levelProgress(300)` is level 2 with an
 * empty bar, not level 1 with a full one. The opposite rule is wrong in the one
 * place it shows: a player who ends a match on exactly 350/350 has earned the
 * level and must be given it.
 *
 * The end screen still gets its crescendo — the bar fills toward the threshold as
 * the count-up runs and flips to the new level on arrival.
 */
export function levelProgress(totalXp) {
    const total = Math.max(0, Math.floor(totalXp || 0));
    // Largest tabled level whose threshold we have reached, then extrapolate.
    let level = 1;
    if (total >= LEVEL_XP[MAX_TABLE_LEVEL - 1]) {
        level = MAX_TABLE_LEVEL + Math.floor((total - LEVEL_XP[MAX_TABLE_LEVEL - 1]) / TAIL_COST);
    }
    else {
        let lo = 0;
        let hi = MAX_TABLE_LEVEL - 1;
        while (lo < hi) {
            const mid = (lo + hi + 1) >> 1;
            if (LEVEL_XP[mid] <= total)
                lo = mid;
            else
                hi = mid - 1;
        }
        level = lo + 1;
    }
    const required = xpCostOfLevel(level);
    const intoLevel = total - xpForLevel(level);
    return {
        level,
        nextLevel: level + 1,
        intoLevel,
        required,
        fraction: required > 0 ? Math.min(1, intoLevel / required) : 1,
    };
}
/** The level a lifetime total puts a player at — what the badge shows. */
export function levelForXp(totalXp) {
    return levelProgress(totalXp).level;
}
/** True when banking XP moved the player from one level into the next. */
export function crossedLevel(before, after) {
    return levelProgress(after).level > levelProgress(before).level;
}
//# sourceMappingURL=levels.js.map